import { useMemo } from "react";
import { MaterialReactTable, createMRTColumnHelper, useMaterialReactTable } from "material-react-table";
import { useTheme } from '@mui/material/styles';
import { Box, Button, Tooltip, IconButton } from "@mui/material";
import { Delete, GroupAdd } from "@mui/icons-material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { notifyError } from "../../../components/common/toast/ToastFunc";

const columnHelper = createMRTColumnHelper();

const columns = [
  columnHelper.accessor("account_no", {
    header: "Account No",
    size: 250,
  }),
  columnHelper.accessor("account_balance", {
    header: "Balance",
    size: 50,
  }),
  columnHelper.accessor("routing_no", {
    header: "Routing No",
    size: 100,
  }),
];

const Table = (props) => {
  const { data, addAccount, deleteAccount, actionLoading, type } = props;

  const theme = useTheme();

  const buttonTheme = useMemo(() => {
    return {
      color: theme.mainColor, fontSize: 15, fontWeight: "bold", borderRadius: 2, borderColor: theme.mainColor,
      '&:hover': {
        backgroundColor: theme.mainColor, color: 'white', borderRadius: 2, borderColor: theme.mainColor
      },
    }
  }, [theme.mainColor]);

  const handleExportRows = (rows) => {
    try {

      const doc = new jsPDF();
      const tableData = rows.map((row, index) => [
        index + 1,
        row.original["account_no"],
        row.original["account_balance"],
        row.original["routing_no"],
      ]);

      const tableHeaders = ["No.", "Account No", "Balance", "Routing No"];

      autoTable(doc, {
        head: [tableHeaders],
        body: tableData,
      });

      doc.save("accounts_list.pdf");
    } catch (error) {
      notifyError(error.message || "Something went wrong");
    }
  };

  const table = useMaterialReactTable({
    columns,
    data,
    state: { isSaving: actionLoading },
    muiCircularProgressProps: {
      color: "primary",
      thickness: 5,
      size: 55,
    },
    muiSkeletonProps: {
      animation: "pulse",
      height: 28,
    },
    enableFullScreenToggle: false,
    enableRowSelection: true,
    paginationDisplayMode: "pages",
    enableEditing: true,
    muiPaginationProps: {
      rowsPerPageOptions: [5, 10, 15, 20, 30],
    },
    positionToolbarAlertBanner: "bottom",
    muiTableHeadCellProps: {
      sx: {
        fontSize: "18px",
      },
    },
    muiTableBodyCellProps: ({ row }) => ({
      sx: {
        fontSize: "16px",
        fontWeight: row.getIsSelected() ? "bold" : "normal",
      },
    }),
    renderRowActions: ({ row, table }) => (
      <Box sx={{ display: "flex", gap: "1rem" }}>
        <Tooltip title='Delete Record' placement="left">
          <IconButton
            color='error'
            onClick={() => deleteAccount(row.original.account_no)}
            disabled={actionLoading}
            sx={{ color: theme.mainColor }}
          >
            <Delete />
          </IconButton>
        </Tooltip>
      </Box>
    ),

    renderTopToolbarCustomActions: ({ table }) => (
      <Box
        sx={{
          display: "flex",
          gap: "5px",
          padding: "5px",
          flexWrap: "wrap",
        }}
      >
        <Button
          disabled={
            table.getPrePaginationRowModel().rows.length === 0 || actionLoading
          }
          onClick={() => handleExportRows(table.getPrePaginationRowModel().rows)}
          startIcon={<FileDownloadIcon />}
          variant='outlined'
          sx={{ ...buttonTheme }}
        >
          Export All
        </Button>
        <Button
          disabled={
            (!table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()) || actionLoading
          }
          onClick={() => handleExportRows(table.getSelectedRowModel().rows)}
          startIcon={<FileDownloadIcon />}
          variant='outlined'
          sx={{ ...buttonTheme }}
        >
          Export Sel
        </Button>
      </Box >
    ),
    renderBottomToolbarCustomActions: ({ table }) => (
      <Button variant='outlined' startIcon={<GroupAdd />} disabled={actionLoading} onClick={() => addAccount(type)} sx={{ ...buttonTheme }}>
        Add {type} Account
      </Button>
    ),
  });

  return (
    <MaterialReactTable table={table} />
  )
};

export default Table;
